import { memo, useEffect, useMemo } from 'react';
import { Badge, Box, Flex, Circle, Divider, VStack, Link, Button } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faFile } from '@fortawesome/pro-regular-svg-icons';
import { IOfferTimelineEntry, ApplicantOfferStatus, IApplicantOffer, OfferTimelineStageType, ApplicantStatusReason } from 'api-paths-cas';
import { formatDateTimeSafe } from '@/utilities/dateHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { getOfferTimeline } from '@/store/apply/actions';
import { ApplicationState } from '@/store';
import { getApplicationOfferBadgeProps } from '@/utilities/getApplicationOfferBadgeProps';
import { timelineStageConfig } from './timelineStageConfig';
import { systemGeneratedApplicantStatusReasonLabels, userGeneratedApplicantStatusReasonLabels } from 'apply-data';
const ISSUE_STAGE_TYPES = new Set<OfferTimelineStageType>([
  OfferTimelineStageType.Conditional,
  OfferTimelineStageType.ConditionalReissued,
  OfferTimelineStageType.Unconditional,
  OfferTimelineStageType.UnconditionalReissued,
]);

type OfferLetterLink = {
  label: string;
  url: string;
};

const getStatusReasonLabel = (reason?: ApplicantStatusReason): string | null => {
  if (!reason) return null;
  return (
    systemGeneratedApplicantStatusReasonLabels[reason] ?? userGeneratedApplicantStatusReasonLabels[reason] ?? reason
  );
};

// Returns offer-letter URLs in newest-first order, matching `offerTimeline`
function extractOfferLetters(offer?: IApplicantOffer): string[] {
  if (!offer?.offerLetter?.pdfUrl) return [];
  const { offerLetter, previousOfferLetters } = offer;
  return [offerLetter.pdfUrl, ...(previousOfferLetters?.map(({ pdfUrl }) => pdfUrl) ?? [])];
}

const getEntryKey = (entry: IOfferTimelineEntry): string => `${entry.timestamp}-${entry.stageType}`;

// Audit log action format is "Offer {offerId}: {courseName} - {displayText}". Extract the display part.
function getDisplayTitleFromAction(entry: IOfferTimelineEntry, stageConfig: { title: string }): string {
  const action = entry.action ?? '';
  if (action.includes(' - ')) {
    return action.split(' - ').pop() ?? stageConfig.title;
  }
  return action || stageConfig.title;
}

interface TimelineEntryProps {
  entry: IOfferTimelineEntry;
  letterLink?: OfferLetterLink;
  isLast?: boolean;
}

const TimelineEntry = memo(function TimelineEntry({ entry, letterLink, isLast }: TimelineEntryProps) {
  const stageConfig = timelineStageConfig[entry.stageType];
  const icon = stageConfig?.icon ?? faArrowRight;
  const stageReasonLabel = getStatusReasonLabel(entry.stageReason);
  const displayTitle =
    stageReasonLabel &&
    (entry.stageType === OfferTimelineStageType.Withdrawn || entry.stageType === OfferTimelineStageType.Rejected)
      ? `${stageConfig.title} (${stageReasonLabel})`
      : getDisplayTitleFromAction(entry, stageConfig);

  const renderStatusBadges = () => {
    if (!entry.showStageBadges) {
      return null;
    }

    const fromProps = getApplicationOfferBadgeProps(entry.stageFrom as ApplicantOfferStatus);
    const toProps = entry.stageTo ? getApplicationOfferBadgeProps(entry.stageTo as ApplicantOfferStatus) : null;

    return (
      <Box mb={1}>
        <Badge colorScheme={fromProps.colorScheme} variant="subtle" size="xs">
          {fromProps.children}
        </Badge>
        {toProps && (
          <>
            {' '}
            <FontAwesomeIcon icon={faArrowRight} className="utility-icon" size="xs" />{' '}
            <Badge colorScheme={toProps.colorScheme} variant="subtle" size="xs">
              {toProps.children}
            </Badge>
          </>
        )}
      </Box>
    );
  };

  return (
    <Flex align="flex-start" pb={4}>
      <Box position="relative" display="flex" flexDirection="column" alignItems="center" mr={3} alignSelf="stretch">
        <Circle size={4} bg="gray.500" color="white">
          <FontAwesomeIcon icon={icon} size="xs" fixedWidth />
        </Circle>
        {!isLast && (
          <Divider orientation="vertical" position="absolute" top={4} bottom={0} opacity={1} borderWidth={0.5} />
        )}
      </Box>

      <Box>
        <Box fontSize="sm" lineHeight={5} mb={1}>
          {displayTitle}
        </Box>

        {renderStatusBadges()}

        {letterLink && (
          <Button
            as={Link}
            href={letterLink.url}
            isExternal
            variant="link"
            size="sm"
            leftIcon={<FontAwesomeIcon icon={faFile} className="utility-icon" />}
          >
            {letterLink.label}
          </Button>
        )}

        <Box fontSize="xs" lineHeight={4} color="muted">
          {formatDateTimeSafe(entry.timestamp, 'DD MMM YYYY HH:mm')}
        </Box>
      </Box>
    </Flex>
  );
});

interface TimelineProps {
  uni: string;
  userId: string;
  applicationId: string;
  offerId: string;
}

export const OfferTimeline = memo(function Timeline({ uni, userId, applicationId, offerId }: TimelineProps) {
  const dispatch = useDispatch();
  const { offerTimeline, offer } = useSelector((state: ApplicationState) => state.apply);
  useEffect(() => {
    if (uni && userId && applicationId && offerId) {
      dispatch(getOfferTimeline(uni, userId, applicationId, offerId));
    }
  }, [uni, userId, applicationId, offerId, dispatch]);

  const offerLetterUrls = useMemo(() => extractOfferLetters(offer), [offer]);

  const letterLinkMap = useMemo(() => {
    const map = new Map<string, OfferLetterLink>();

    if (!offerTimeline?.length || !offerLetterUrls.length) {
      return map;
    }

    // Both offerTimeline and offerLetterUrls are newest-first.
    let letterIndex = 0;
    for (const entry of offerTimeline) {
      if (!ISSUE_STAGE_TYPES.has(entry.stageType)) {
        continue;
      }

      const pdfUrl = offerLetterUrls[letterIndex];
      letterIndex += 1;

      if (!pdfUrl) {
        continue;
      }

      const config = timelineStageConfig[entry.stageType];
      const labelStage = config.subtitle;

      map.set(getEntryKey(entry), {
        label: `${labelStage} offer letter`,
        url: pdfUrl,
      });
    }

    return map;
  }, [offerTimeline, offerLetterUrls]);

  if (!offerTimeline || !offerTimeline.length) {
    return (
      <Box p={4} textAlign="center" color="muted" fontSize="sm" data-selector="offer-timeline-empty">
        No timeline entries found
      </Box>
    );
  }

  return (
    <VStack spacing={0} py={2} align="stretch">
      {offerTimeline.map((entry, index) => {
        const letterLink = letterLinkMap.get(getEntryKey(entry));
        const isLast = index === offerTimeline.length - 1;
        return (
          <TimelineEntry key={`${entry.timestamp}-${index}`} entry={entry} letterLink={letterLink} isLast={isLast} />
        );
      })}
    </VStack>
  );
});
